import { processSearchResults } from '../../search-results-processor';
import { useAuth } from '../../auth.context';
import { useMemo } from 'react';
import {
  SearchJob,
  PlayerData,
  SearchStatus
} from '../types/kyp-search.types';

export interface AsyncSearchServiceDeps {
  getToken: () => string | null;
  onStatusUpdate: (job: SearchJob) => void;
  onResultsReady: (results: PlayerData) => void;
  onError: (error: string) => void;
  onLoading: (loading: boolean) => void;
}

export class AsyncSearchService {
  private pollingRef: number | null = null;
  private pollingTimeoutRef: number | null = null;
  private pollingErrorCount = 0;
  private abortController: AbortController | null = null;

  constructor(private deps: AsyncSearchServiceDeps) {
    this.abortController = new AbortController();
  }

  async startAsyncSearch(playerId: string, playerName?: string): Promise<{
    searchId: number;
    status: SearchStatus;
  }> {
    if (!playerId.trim()) {
      throw new Error('Please enter a player ID');
    }

    try {
      // Cancel any previous requests
      this.abort();
      
      // Create a new abort controller
      this.abortController = new AbortController();
      
      // Stop any ongoing polling
      this.stopPolling();
      
      // Set loading state
      this.deps.onLoading(true);
      
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Error: Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const searchResponse = await fetch(`${baseUrl}/ai/v1/player/search/async`, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({ 
          player_id: playerId,
          player_name: playerName, // Include player name in the request
          force_refresh: true // Add force_refresh parameter to ensure a fresh search
        }),
        signal: this.abortController.signal
      });
      
      if (!searchResponse.ok) {
        throw new Error(`HTTP error! status: ${searchResponse.status}`);
      }
      
      const searchData = await searchResponse.json();
      
      if (searchData.status === 'error') {
        throw new Error(searchData.error_message || 'Failed to start search');
      }
      
      // Store the search ID and initialize search state
      const newSearchId = searchData.search_id;
      console.log(`Started async search with ID: ${newSearchId}`);
      
      // Set initial job state
      const initialJob: SearchJob = {
        id: newSearchId,
        player_id: playerId,
        status: 'in_progress',
        progress: {
          progress_percentage: 5,
          current_step: 'initialize_database',
          step_progress: 10,
          completed_steps: []
        }
      };
      
      // Update job status
      this.deps.onStatusUpdate(initialJob);
      
      // Start polling with the new search ID
      this.startPolling(newSearchId);
      
      return {
        searchId: newSearchId,
        status: 'in_progress'
      };
    } catch (error) {
      // Don't set error if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Search request was aborted');
        return {
          searchId: 0,
          status: 'cancelled'
        };
      }
      
      console.error('Search error:', error);
      this.deps.onError(`Error: ${error instanceof Error ? error.message : 'Failed to start search'}`);
      this.deps.onLoading(false);
      
      throw error;
    }
  }
  
  async checkSearchStatus(searchId: number): Promise<SearchJob | null> {
    if (!searchId) {
      console.log('No search ID provided to checkSearchStatus');
      return null;
    }
    
    try {
      const token = this.deps.getToken();
      if (!token) {
        this.deps.onError('Error: Not authenticated');
        this.stopPolling();
        return null;
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      console.log(`Fetching status for search ID: ${searchId}`);
      const statusResponse = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/status`, {
        headers: requestHeaders,
        signal: this.abortController?.signal
      });
      
      if (!statusResponse.ok) {
        throw new Error(`HTTP error! status: ${statusResponse.status}`);
      }
      
      const statusData = await statusResponse.json();
      
      if (statusData.status === 'error') {
        throw new Error(statusData.error_message || 'Failed to check search status');
      }
      
      // Update search job with the entire job object from API
      if (statusData.job) {
        // First check if there's a top-level progress object (new format)
        let progressPercentage = 0;
        let currentStep = 'initialize_database';
        let stepProgress = 0;
        let completedSteps: string[] = [];
        let stepDescription = 'Processing search';
        
        // Check for the top-level progress object first (new format)
        if (statusData.progress) {
          console.log('Found top-level progress object:', statusData.progress);
          
          // Use the top-level progress information directly
          progressPercentage = statusData.progress.progress_percentage || 0;
          currentStep = statusData.progress.current_step || 'initialize_database';
          stepProgress = statusData.progress.step_progress || 0;
          completedSteps = statusData.progress.completed_steps || [];
          stepDescription = statusData.progress.step_description || 'Processing search';
        }
        // If no top-level progress, check for job.progress (old format)
        else if (statusData.job.progress) {
          // Handle the job.progress format with total_urls_found, filtered_urls_count, etc.
          if ('total_urls_found' in statusData.job.progress) {
            const jobProgress = statusData.job.progress;
            
            // For completed jobs, set progress to 100%
            if (statusData.job.status === 'completed') {
              progressPercentage = 100;
              currentStep = 'generate_summary';
              stepProgress = 100;
              completedSteps = [
                'initialize_database',
                'lookup_player',
                'create_search_record',
                'generate_queries',
                'perform_web_searches',
                'filter_urls',
                'enqueue_scrape_jobs',
                'wait_for_scraping',
                'process_scraped_content',
                'validate_content',
                'persist_articles',
                'process_with_llm',
                'update_search_record',
                'generate_summary'
              ];
              stepDescription = 'Search completed successfully';
            } 
            // For in-progress jobs, estimate progress based on available metrics
            else if (statusData.job.status === 'in_progress') {
              // Calculate progress based on scraping and processing progress
              const totalUrlsFound = jobProgress.total_urls_found || 0;
              const filteredUrlsCount = jobProgress.filtered_urls_count || 0;
              const successfulScrapesCount = jobProgress.successful_scrapes_count || 0;
              const validArticlesCount = jobProgress.valid_articles_count || 0;
              
              if (totalUrlsFound > 0) {
                // We've started searching
                completedSteps.push('initialize_database');
                completedSteps.push('lookup_player');
                completedSteps.push('create_search_record');
                completedSteps.push('generate_queries');
                completedSteps.push('perform_web_searches');
                
                if (filteredUrlsCount > 0) {
                  // We've filtered URLs
                  completedSteps.push('filter_urls');
                  completedSteps.push('enqueue_scrape_jobs');
                  currentStep = 'wait_for_scraping';
                  
                  if (successfulScrapesCount > 0) {
                    // We've started scraping
                    stepProgress = Math.min(Math.round((successfulScrapesCount / filteredUrlsCount) * 100), 100);
                    
                    if (successfulScrapesCount >= filteredUrlsCount || validArticlesCount > 0) {
                      // Scraping is complete or we have valid articles
                      completedSteps.push('wait_for_scraping');
                      currentStep = 'process_scraped_content';
                      
                      if (validArticlesCount > 0) {
                        // We've processed content
                        completedSteps.push('process_scraped_content');
                        completedSteps.push('validate_content');
                        completedSteps.push('persist_articles');
                        currentStep = 'process_with_llm';
                        stepProgress = 50; // Estimate for LLM processing
                        
                        // If we've been running for more than 30 seconds, assume we're further along
                        if (jobProgress.execution_time_seconds > 30) {
                          stepProgress = 80;
                        }
                      }
                    }
                  }
                }
                
                // Calculate overall progress percentage based on completed steps
                progressPercentage = Math.min(
                  Math.round((completedSteps.length / 14) * 100) + (stepProgress / 100 * (100 / 14)),
                  95 // Cap at 95% until fully complete
                );
              }
            }
          }
        }
        // Handle the legacy format with progress_percentage directly on the job
        else if (statusData.job.progress_percentage !== undefined) {
          progressPercentage = statusData.job.progress_percentage;
          currentStep = statusData.job.current_step || 'initialize_database';
          stepProgress = statusData.job.step_progress || 0;
          if (statusData.job.step_details && statusData.job.step_details.completed_steps) {
            completedSteps = statusData.job.step_details.completed_steps;
          }
        }
        
        // Create a complete job object with progress information
        const completeJob: SearchJob = {
          ...statusData.job,
          // Add calculated progress information
          progress: {
            progress_percentage: progressPercentage,
            current_step: currentStep,
            step_progress: stepProgress,
            completed_steps: completedSteps,
            step_description: stepDescription
          }
        };
        
        console.log('Received job status:', completeJob.status, 
          `Progress: ${completeJob.progress?.progress_percentage}%, Step: ${completeJob.progress?.current_step}`);
        
        // Update the context with the job
        this.deps.onStatusUpdate(completeJob);
        
        // Handle job completion
        if (completeJob.status === 'completed') {
          console.log('Search completed, stopping polling');
          this.stopPolling();
          
          // Fetch results
          await this.getSearchResults(completeJob.id!);
        }
        
        // Handle failure or cancellation
        if (completeJob.status === 'failed' || completeJob.status === 'cancelled') {
          console.log(`Search ${completeJob.status}, stopping polling`);
          
          if (completeJob.status === 'cancelled') {
            // For cancelled searches, use a friendly message
            this.deps.onError('Search cancelled by user');
          } else {
            // For failed searches, show the error from the API
            this.deps.onError(`Search failed: ${completeJob.error_message || 'Unknown error'}`);
          }
          
          this.stopPolling();
          this.deps.onLoading(false);
        }
        
        return completeJob;
      } else {
        console.error('No job data found in status response');
        return null;
      }
    } catch (error) {
      console.error('Error checking search status:', error);
      
      // If we encounter an error checking status multiple times, eventually give up
      if (this.pollingErrorCount >= 5) {
        console.error('Too many errors checking search status, stopping polling');
        this.stopPolling();
        this.deps.onError(`Error checking search status: ${error instanceof Error ? error.message : 'Unknown error'}`);
        this.deps.onLoading(false);
      } else {
        this.pollingErrorCount += 1;
      }
      
      return null;
    }
  }
  
  async getSearchResults(searchId: number, retryCount: number = 0): Promise<PlayerData | null> {
    if (!searchId) {
      console.log('No search ID to get results for');
      return null;
    }
    
    try {
      const token = this.deps.getToken();
      if (!token) {
        this.deps.onError('Error: Not authenticated');
        return null;
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // First, get the search job details to ensure we have the player ID
      console.log(`Fetching search job details for ID: ${searchId}`);
      const jobResponse = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/status`, {
        headers: requestHeaders,
        signal: this.abortController?.signal
      });
      
      if (!jobResponse.ok) {
        throw new Error(`HTTP error! status: ${jobResponse.status}`);
      }
      
      const jobData = await jobResponse.json();
      
      // Now fetch the actual results
      console.log(`Fetching results for search ID: ${searchId} (attempt ${retryCount + 1})`);
      const resultsResponse = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/results`, {
        headers: requestHeaders,
        signal: this.abortController?.signal
      });
      
      if (!resultsResponse.ok) {
        throw new Error(`HTTP error! status: ${resultsResponse.status}`);
      }
      
      const resultsData = await resultsResponse.json();
      
      if (resultsData.status === 'error') {
        throw new Error(resultsData.error_message || 'Failed to get search results');
      }
      
      if (resultsData.status === 'pending') {
        console.log('Search still in progress, continuing to poll');
        
        // If we've been retrying for a while, implement a retry with backoff
        if (retryCount < 3) {
          // Wait and retry with exponential backoff
          const delay = Math.pow(2, retryCount) * 2000; // 2s, 4s, 8s
          console.log(`Will retry fetching results in ${delay}ms (attempt ${retryCount + 1}/3)`);
          
          return new Promise(resolve => {
            setTimeout(async () => {
              const results = await this.getSearchResults(searchId, retryCount + 1);
              resolve(results);
            }, delay);
          });
        } else {
          console.warn(`Exceeded maximum retries (${retryCount}) for fetching results`);
          return null;
        }
      }
      
      let processedData: PlayerData;
      
      // Check if we have results in the job object
      if (resultsData.job && resultsData.job.results) {
        console.log('Found results in job object');
        
        // Process the results from the job object
        const { player_id: resultPlayerId, player_name, ...jobResults } = resultsData.job;
        
        // Process the results
        processedData = processSearchResults(jobResults.results);
        
        // Ensure debug section is preserved if it exists
        if (jobResults.results.debug && jobResults.results.debug.all_articles) {
          if (!processedData.debug) {
            processedData.debug = {};
          }
          processedData.debug.all_articles = jobResults.results.debug.all_articles;
          console.log('Preserved debug.all_articles with', jobResults.results.debug.all_articles.length, 'items');
        }
      } else {
        // Process the results - remove status and message fields before setting as player data
        const { status, message, player_id: resultPlayerId, ...playerDataFields } = resultsData;
        
        // Process each field to ensure proper JSON parsing
        processedData = {};
        
        // Process each category in the results
        Object.entries(playerDataFields).forEach(([key, value]) => {
          if (typeof value === 'string') {
            try {
              // Try to parse the string as JSON
              const parsedValue = JSON.parse(value);
              processedData[key as keyof PlayerData] = parsedValue;
            } catch (e) {
              // If parsing fails, use the string value directly
              processedData[key as keyof PlayerData] = { summary: value };
            }
          } else {
            // If it's already an object, use it directly
            processedData[key as keyof PlayerData] = value as any;
          }
        });
      }
      
      console.log('Processed data:', processedData);
      
      // Notify that results are ready
      this.deps.onResultsReady(processedData);
      
      // Clear error messages
      this.deps.onError('');
      
      // Set loading to false
      this.deps.onLoading(false);
      
      return processedData;
    } catch (error) {
      console.error('Error getting search results:', error);
      
      // Implement retry logic for errors
      if (retryCount < 3) {
        // Wait and retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 2000; // 2s, 4s, 8s
        console.log(`Will retry fetching results in ${delay}ms after error (attempt ${retryCount + 1}/3)`);
        
        return new Promise(resolve => {
          setTimeout(async () => {
            const results = await this.getSearchResults(searchId, retryCount + 1);
            resolve(results);
          }, delay);
        });
      }
      
      this.deps.onError(`Error: ${error instanceof Error ? error.message : 'Failed to get search results'}`);
      this.deps.onLoading(false);
      return null;
    }
  }
  
  async cancelSearch(searchId: number): Promise<void> {
    if (!searchId) {
      console.log('No search ID to cancel');
      return;
    }
    
    try {
      // Stop polling immediately
      this.stopPolling();
      this.deps.onLoading(false);
      
      // Set a clear cancellation message
      this.deps.onError('Search cancelled by user');
      
      // Update job status
      this.deps.onStatusUpdate({
        id: searchId,
        status: 'cancelled'
      });
      
      const token = this.deps.getToken();
      if (!token) {
        this.deps.onError('Error: Not authenticated');
        return;
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      const cancelResponse = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}`, {
        method: 'DELETE',
        headers: requestHeaders,
        signal: this.abortController?.signal
      });
      
      if (!cancelResponse.ok) {
        throw new Error(`HTTP error! status: ${cancelResponse.status}`);
      }
      
      const cancelData = await cancelResponse.json();
      
      if (cancelData.status === 'error') {
        throw new Error(cancelData.error_message || 'Failed to cancel search');
      }
      
      // Don't update the error message if the cancellation was successful
      // The original "Search cancelled by user" message is clearer
    } catch (error) {
      console.error('Error cancelling search:', error);
      this.deps.onError(`Error: ${error instanceof Error ? error.message : 'Failed to cancel search'}`);
    }
  }
  
  startPolling(searchId: number): void {
    // Clear any existing polling
    this.stopPolling();
    
    console.log(`Starting polling for search ID: ${searchId} with interval 3000ms`);
    
    // Set a timeout to stop polling after a maximum time (15 minutes)
    const maxPollingTime = 15 * 60 * 1000; // 15 minutes in milliseconds
    const pollingTimeout = setTimeout(() => {
      console.log(`Polling timeout reached for search ID: ${searchId}`);
      this.stopPolling();
      
      this.deps.onError('KYP Search timed out due to a system error.');
      
      // Update search job status to failed
      this.deps.onStatusUpdate({
        id: searchId,
        status: 'failed',
        error_message: 'KYP Search timed out due to a system error.'
      });
      
      this.deps.onLoading(false);
    }, maxPollingTime);
    
    // Store the timeout ID for cleanup
    this.pollingTimeoutRef = pollingTimeout;
    
    // Immediately check status once
    this.checkSearchStatus(searchId).catch(error => {
      console.error('Error checking search status during polling start:', error);
    });
    
    // Start new polling
    this.pollingRef = window.setInterval(() => {
      this.checkSearchStatus(searchId).catch(error => {
        console.error('Error checking search status during polling:', error);
      });
    }, 3000);
  }
  
  stopPolling(): void {
    if (this.pollingRef) {
      console.log('Stopping polling');
      window.clearInterval(this.pollingRef);
      this.pollingRef = null;
    }
    
    // Also clear the polling timeout if it exists
    if (this.pollingTimeoutRef) {
      console.log('Clearing polling timeout');
      window.clearTimeout(this.pollingTimeoutRef);
      this.pollingTimeoutRef = null;
    }
    
    // Reset the error count
    this.pollingErrorCount = 0;
  }
  
  abort(): void {
    if (this.abortController) {
      console.log('Aborting pending requests');
      this.abortController.abort();
      this.abortController = new AbortController();
    }
    
    // Also stop polling
    this.stopPolling();
  }
}

export const useAsyncSearchService = (deps: Omit<AsyncSearchServiceDeps, 'getToken'>) => {
  const { getToken } = useAuth();
  
  return useMemo(() => {
    return new AsyncSearchService({
      ...deps,
      getToken
    });
  }, [deps, getToken]);
};